(function initializeAlvauraPurchasePath(root, createApi) {
  'use strict';

  const api = createApi();

  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.AlvauraPurchasePath = api;
  if (!root?.customElements || !root.HTMLElement) return;

  const nextFrame = (callback) => {
    if (typeof root.requestAnimationFrame === 'function') {
      root.requestAnimationFrame(callback);
    } else {
      setTimeout(callback, 0);
    }
  };

  const selectedControlValue = (group) => {
    const select = group?.matches?.('select') ? group : group?.querySelector?.('select');
    if (select) return select.value || '';
    return group?.querySelector?.('input:checked')?.value || '';
  };

  const optionGroups = (picker) =>
    Array.from(picker?.querySelectorAll?.('.variant-option') || []).filter(
      (group, index, groups) => groups.indexOf(group) === index
    );

  const optionIndexForGroup = (group, optionNames, visibleIndex, visibleCount) => {
    const rawName =
      group?.dataset?.alvauraOptionName ||
      group?.querySelector?.('legend')?.textContent?.split(':')[0] ||
      group?.querySelector?.('label')?.textContent ||
      '';
    const normalizedName = api.normalizeWhitespace(rawName).toLowerCase();
    const namedIndex = optionNames.findIndex(
      (name) => api.normalizeWhitespace(name).toLowerCase() === normalizedName
    );
    return namedIndex >= 0 ? namedIndex : visibleCount === optionNames.length ? visibleIndex : -1;
  };

  const elementForSection = (container, sectionId) =>
    Array.from(container?.querySelectorAll?.('alvaura-availability') || []).find(
      (element) => String(element.dataset.sectionId) === String(sectionId)
    );

  if (!root.customElements.get('alvaura-availability')) {
    root.customElements.define(
      'alvaura-availability',
      class AlvauraAvailability extends root.HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;

          this.sectionId = this.dataset.sectionId;
          this.productInfo = this.closest('product-info');
          this.responseSectionId = this.productInfo?.dataset.originalSection || this.sectionId;
          this.sectionRoot =
            this.productInfo?.closest('.main-product-information') || this.productInfo || this.parentElement;
          this.productForm = null;
          this.resolveProductForm();
          this.message = this.querySelector('[data-availability-message]');
          this.fulfillmentProperty = this.querySelector('[data-alvaura-fulfillment-property]');
          this.requireExplicitSize = this.dataset.requireExplicitSize === 'true';
          this.initialView = this.requireExplicitSize
            ? ''
            : new URLSearchParams(root.location?.search || '').get('view') || '';
          this.explicitSizeSelected = !api.requiresExplicitSizeSelection(
            this.requireExplicitSize,
            this.readVariantPayload()
          );
          this.manualSelectionSeen = false;
          this.defaultSelectionApplied = false;
          this.defaulting = false;
          this.currentRecord = null;
          this.responseSequence = 0;

          this.onPickerInput = (event) => {
            const picker = event.target.closest?.('variant-selects[data-context^="main"]');
            if (!picker || !this.productInfo?.contains(picker)) return;
            const group = event.target.closest?.('.variant-option');
            const optionName = group?.dataset.alvauraOptionName || group?.querySelector('legend, label')?.textContent;

            if (!this.defaulting && event.isTrusted) this.manualSelectionSeen = true;
            if (this.requireExplicitSize && api.isSizeOptionName(optionName)) {
              this.explicitSizeSelected = true;
            }

            this.responseSequence += 1;
            this.enterPending('variant_selection_pending');
            setTimeout(() => {
              const selectedRecord = this.recordForSelectedControls();
              if (selectedRecord) {
                this.commitRecord(selectedRecord);
              } else if (!this.currentFormVariantId()) {
                this.commitRecord(api.unavailableRecord());
              }
            }, 0);
          };

          this.onVariantIdChange = (event) => {
            const variantInput = event.target.closest?.(
              `#product-form-${this.sectionId} input[name="id"].product-variant-id`
            );
            if (!variantInput || !this.productInfo?.contains(variantInput)) return;
            if (!variantInput.value) {
              this.commitRecord(this.recordForSelectedControls() || api.unavailableRecord());
            }
          };

          this.onSubmit = (event) => {
            const selectedRecord = this.recordForSelectedControls();
            if (selectedRecord) this.commitRecord(selectedRecord);
            if (
              this.explicitSizeSelected &&
              api.canSubmitPurchase(this.dataset.state, this.currentFormVariantId(), this.currentRecord?.variantId)
            ) {
              return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();
          };

          this.productInfo?.addEventListener('input', this.onPickerInput, true);
          this.productInfo?.addEventListener('change', this.onPickerInput, true);
          this.productInfo?.addEventListener('change', this.onVariantIdChange, true);
          this.productForm?.addEventListener('submit', this.onSubmit, true);

          if (root.MutationObserver && this.productInfo) {
            this.formObserver = new root.MutationObserver(() => this.reconcileCurrentForm());
            this.formObserver.observe(this.productInfo, {
              attributes: true,
              attributeFilter: ['value', 'disabled'],
              childList: true,
              subtree: true,
            });
          }
          this.formReconciliationInterval = root.setInterval?.(
            () => this.reconcileCurrentForm(),
            250
          );

          const subscribeTo = root.subscribe || (typeof subscribe === 'function' ? subscribe : null);
          const events = root.PUB_SUB_EVENTS || (typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null);
          if (subscribeTo && events?.variantChange) {
            this.unsubscribeVariant = subscribeTo(events.variantChange, ({ data }) => {
              if (String(data?.sectionId) !== String(this.responseSectionId)) return;
              this.queueVariantResponse(data?.variant, data?.html);
            });
          }

          this.onVariantUpdate = (event) => {
            const sourceId = event?.detail?.sourceId;
            if (
              String(sourceId) !== String(this.productInfo?.id) &&
              String(sourceId) !== String(this.sectionId)
            ) {
              return;
            }
            this.queueVariantResponse(event?.detail?.resource, event?.detail?.data?.html);
          };
          document.addEventListener('variant:update', this.onVariantUpdate);

          if (this.requireExplicitSize) this.clearInitialSizeSelection();

          const initialRecord = this.readRecord(this);
          if (this.requireExplicitSize && this.hasSizeOption()) {
            this.enterPending('size_selection_required');
          } else {
            this.commitRecord(initialRecord);
            nextFrame(() => {
              if (!this.applyPreferredDefault()) {
                this.commitRecord(this.recordForSelectedControls() || initialRecord);
              }
            });
          }
        }

        disconnectedCallback() {
          this.unsubscribeVariant?.();
          document.removeEventListener('variant:update', this.onVariantUpdate);
          this.productInfo?.removeEventListener('input', this.onPickerInput, true);
          this.productInfo?.removeEventListener('change', this.onPickerInput, true);
          this.productInfo?.removeEventListener('change', this.onVariantIdChange, true);
          this.productForm?.removeEventListener('submit', this.onSubmit, true);
          this.formObserver?.disconnect();
          if (this.formReconciliationInterval) root.clearInterval?.(this.formReconciliationInterval);
          this.initialized = false;
        }

        currentFormVariantId() {
          return this.resolveProductForm()?.querySelector('input[name="id"].product-variant-id')?.value || '';
        }

        resolveProductForm() {
          const nextForm = this.productInfo?.querySelector?.(`#product-form-${this.sectionId}`) || null;
          if (nextForm) this.productForm = nextForm;
          return this.productForm;
        }

        resolveFulfillmentProperty() {
          const nextProperty = this.querySelector?.('[data-alvaura-fulfillment-property]') || null;
          if (nextProperty) this.fulfillmentProperty = nextProperty;
          return this.fulfillmentProperty || null;
        }

        readRecord(source) {
          try {
            return JSON.parse(source?.querySelector('[data-selected-purchase-state]')?.textContent || '');
          } catch (_error) {
            return null;
          }
        }

        readVariantPayload() {
          try {
            return JSON.parse(this.querySelector('[data-alvaura-product-variants]')?.textContent || '');
          } catch (_error) {
            return null;
          }
        }

        currentVariantPicker() {
          return this.productInfo?.querySelector('variant-selects[data-context^="main"]');
        }

        selectedOptionValues(payload = this.readVariantPayload()) {
          const optionNames = Array.isArray(payload?.optionNames) ? payload.optionNames : [];
          const selectedValues = new Array(optionNames.length).fill('');
          const baseRecord = this.currentRecord || this.readRecord(this);
          const baseVariant = payload?.variants?.find((variant) =>
            api.variantIdsMatch(variant.id, baseRecord?.variantId || this.currentFormVariantId())
          );

          if (Array.isArray(baseVariant?.options)) {
            baseVariant.options.forEach((value, index) => {
              if (index < selectedValues.length) selectedValues[index] = value;
            });
          }

          const groups = optionGroups(this.currentVariantPicker());
          groups.forEach((group, visibleIndex) => {
            const value = selectedControlValue(group);
            if (!value) return;
            const optionIndex = optionIndexForGroup(group, optionNames, visibleIndex, groups.length);
            if (optionIndex >= 0) selectedValues[optionIndex] = value;
          });

          return selectedValues;
        }

        recordForSelectedControls() {
          const payload = this.readVariantPayload();
          if (!payload?.variants?.length) return null;

          const selectedValues = this.selectedOptionValues(payload);
          if (selectedValues.some((value) => !value)) return null;

          const variant = payload.variants.find(
            (candidate) =>
              Array.isArray(candidate.options) &&
              candidate.options.length === selectedValues.length &&
              candidate.options.every((value, index) => value === selectedValues[index])
          );
          return variant ? api.recordForVariant(variant) : null;
        }

        queueVariantResponse(variant, sourceHtml) {
          const sequence = ++this.responseSequence;
          nextFrame(() => {
            if (sequence !== this.responseSequence) return;
            const currentVariantId = this.currentFormVariantId();
            if (variant?.id && currentVariantId && !api.variantIdsMatch(variant.id, currentVariantId)) return;
            this.commitVariantResponse(variant, sourceHtml);
          });
        }

        commitVariantResponse(variant, sourceHtml) {
          if (!sourceHtml?.querySelector) {
            this.commitRecord(api.unavailableRecord());
            return;
          }

          const source = elementForSection(sourceHtml, this.responseSectionId);
          const record = this.readRecord(source);
          if (!api.variantIdsMatch(variant?.id, record?.variantId)) {
            this.commitRecord(api.unavailableRecord());
            return;
          }

          this.updateComposition(sourceHtml);
          this.updateFamilySelector(sourceHtml);

          if (this.requireExplicitSize && !this.explicitSizeSelected) {
            this.enterPending('size_selection_required');
            return;
          }

          this.commitRecord(record, source);
        }

        updateComposition(sourceHtml) {
          const sourceComposition = Array.from(
            sourceHtml?.querySelectorAll?.('[data-alvaura-composition]') || []
          ).find(
            (element) => String(element.dataset.sectionId) === String(this.responseSectionId)
          );
          const currentComposition = Array.from(
            document.querySelectorAll('[data-alvaura-composition]')
          ).find(
            (element) =>
              String(element.dataset.sectionId) === String(this.sectionId) &&
              this.productInfo?.contains(element)
          );
          if (sourceComposition && currentComposition) {
            currentComposition.textContent = sourceComposition.textContent;
          }
        }

        updateFamilySelector(sourceHtml) {
          const sourceSelector = Array.from(
            sourceHtml?.querySelectorAll?.('[data-alvaura-family-selector]') || []
          ).find(
            (element) => String(element.dataset.sectionId) === String(this.responseSectionId)
          );
          const currentSelector = Array.from(
            document.querySelectorAll('[data-alvaura-family-selector]')
          ).find(
            (element) =>
              String(element.dataset.sectionId) === String(this.sectionId) &&
              this.productInfo?.contains(element)
          );
          if (!sourceSelector || !currentSelector) return;

          currentSelector.innerHTML = sourceSelector.innerHTML;
          currentSelector.dataset.currentMetalLabel = sourceSelector.dataset.currentMetalLabel || '';
          currentSelector.dataset.currentGemstoneLabel = sourceSelector.dataset.currentGemstoneLabel || '';
        }

        enterPending(code) {
          this.dataset.state = api.STATES.PENDING;
          this.dataset.configurationCode = code || '';
          this.removeAttribute('data-ready');
          this.setAttribute('aria-busy', 'true');
          this.updateFormAndButtons(api.viewModelForState(api.STATES.PENDING), null);
          this.updateMessage('');
        }

        commitRecord(record, source = this) {
          const validation = api.validatePurchaseRecord(record);
          const safeRecord = validation.valid ? record : api.unavailableRecord();
          const model = api.viewModelForState(safeRecord.state);
          const sourceTemplate = source?.querySelector?.(
            `template[data-availability-template="${safeRecord.state}"]`
          );
          if (!sourceTemplate) {
            this.dataset.configurationCode = 'availability_copy_missing';
            const unavailableTemplate = this.querySelector?.(
              'template[data-availability-template="unavailable"]'
            );
            this.updateMessage(unavailableTemplate || api.COPY.unavailableStatus);
            this.updateFormAndButtons(api.viewModelForState(api.STATES.UNAVAILABLE), null);
            return false;
          }

          this.currentRecord = safeRecord;
          this.dataset.state = safeRecord.state;
          this.dataset.configurationCode = validation.valid ? '' : validation.reason;
          this.setAttribute('data-ready', '');
          this.removeAttribute('aria-busy');
          this.updateMessage(sourceTemplate);
          this.updateFormAndButtons(model, safeRecord.variantId);
          this.syncStickyPicker();
          this.preserveInitialView(safeRecord.variantId);
          this.scheduleFormReconciliation(safeRecord);
          return validation.valid;
        }

        preserveInitialView(variantId) {
          if (!this.initialView || !root.location || !root.history?.replaceState) return;
          const nextUrl = api.urlWithVariantAndView(root.location.href, variantId, this.initialView);
          if (nextUrl) root.history.replaceState({}, '', nextUrl);
        }

        scheduleFormReconciliation(record) {
          const variantId = api.normalizeVariantId(record?.variantId);
          if (!variantId) return;
          const token = `${variantId}:${++this.responseSequence}`;
          this.formReconciliationToken = token;

          [0, 250, 1000].forEach((delay) => {
            setTimeout(() => {
              if (this.formReconciliationToken !== token) return;
              if (!api.variantIdsMatch(this.currentRecord?.variantId, variantId)) return;
              const selectedRecord = this.recordForSelectedControls();
              if (!api.variantIdsMatch(selectedRecord?.variantId, variantId)) return;
              this.updateFormAndButtons(api.viewModelForState(record.state), variantId);
              this.syncStickyPicker();
              this.preserveInitialView(variantId);
            }, delay);
          });
        }

        reconcileCurrentForm() {
          const variantId = api.normalizeVariantId(this.currentRecord?.variantId);
          if (!variantId) return false;
          const selectedRecord = this.recordForSelectedControls();
          if (!api.variantIdsMatch(selectedRecord?.variantId, variantId)) return false;
          this.syncStickyPicker();

          const model = api.viewModelForState(this.currentRecord.state);
          const input = this.resolveProductForm()?.querySelector('input[name="id"].product-variant-id');
          const fulfillmentProperty = this.resolveFulfillmentProperty();
          const expectedDisabled = !model.purchasable;
          const expectedFulfillmentDisabled = !model.madeToOrder;
          const needsSync =
            !input ||
            !api.variantIdsMatch(input.value, variantId) ||
            input.getAttribute('value') !== variantId ||
            input.disabled !== expectedDisabled ||
            (fulfillmentProperty &&
              (fulfillmentProperty.value !== 'Made to Order' ||
                fulfillmentProperty.getAttribute?.('value') !== 'Made to Order' ||
                fulfillmentProperty.disabled !== expectedFulfillmentDisabled));
          if (!needsSync) return false;

          this.updateFormAndButtons(model, variantId);
          this.preserveInitialView(variantId);
          return true;
        }

        syncStickyPicker() {
          const mainPicker = this.currentVariantPicker();
          const stickyPicker = Array.from(
            document.querySelectorAll('variant-selects[data-context^="sticky"]')
          ).find((picker) => {
            const stickyAtc = picker.closest('sticky-atc');
            return String(stickyAtc?.dataset.stickySectionId || '') === String(this.sectionId);
          });
          if (!mainPicker || !stickyPicker) return false;

          const selectedControls = mainPicker.querySelectorAll(
            'select option[selected], fieldset input[type="radio"]:checked'
          );
          let synchronized = false;

          selectedControls.forEach((mainControl) => {
            const optionValueId = mainControl.dataset.optionValueId;
            if (!optionValueId) return;
            const stickyControl = stickyPicker.querySelector(
              `[data-option-value-id="${optionValueId}"]`
            );
            if (!stickyControl) return;

            if (stickyControl.tagName === 'INPUT' && stickyControl.type === 'radio') {
              const group = stickyControl.closest('.variant-option');
              group?.querySelectorAll('input[type="radio"]').forEach((input) => {
                input.checked = input === stickyControl;
                input.toggleAttribute('checked', input === stickyControl);
              });
              const selectedLabel = group?.querySelector('[data-selected-value]');
              if (selectedLabel) selectedLabel.textContent = stickyControl.value;
              synchronized = true;
            } else if (stickyControl.tagName === 'OPTION') {
              const select = stickyControl.closest('select');
              if (!select) return;
              Array.from(select.options).forEach((option) => {
                option.selected = option === stickyControl;
                option.toggleAttribute('selected', option === stickyControl);
              });
              synchronized = true;
            }
          });

          return synchronized;
        }

        updateMessage(content) {
          if (!this.message) return;
          if (content?.content?.cloneNode && this.message.replaceChildren) {
            this.message.replaceChildren(content.content.cloneNode(true));
          } else if (typeof content?.innerHTML === 'string') {
            this.message.innerHTML = content.innerHTML;
          } else {
            this.message.textContent = String(content || '');
          }
          const hasContent = Boolean(api.normalizeWhitespace(this.message.textContent));
          this.message.hidden = !hasContent;
          this.message.setAttribute('aria-hidden', String(!hasContent));
        }

        updateFormAndButtons(model, variantId) {
          const productForm = this.resolveProductForm();
          const variantInput = productForm?.querySelector('input[name="id"].product-variant-id');
          if (variantInput) {
            if (variantId != null) {
              variantInput.value = String(variantId);
              variantInput.setAttribute('value', String(variantId));
            }
            variantInput.disabled = !model.purchasable;
            if (this.dataset) {
              this.dataset.formVariantId = variantId == null ? '' : String(variantId);
              this.dataset.formSync = 'ready';
            }
          } else if (this.dataset) {
            this.dataset.formVariantId = '';
            this.dataset.formSync = 'missing_input';
          }

          const fulfillmentProperty = this.resolveFulfillmentProperty();
          if (fulfillmentProperty) {
            fulfillmentProperty.value = 'Made to Order';
            fulfillmentProperty.setAttribute?.('value', 'Made to Order');
            fulfillmentProperty.disabled = !model.madeToOrder;
          }

          const buttonIds = [
            `ProductSubmitButton-${this.sectionId}`,
            `StickyCart-ProductSubmitButton-${this.sectionId}`,
          ];
          buttonIds.forEach((id) => {
            const button = document.getElementById(id);
            if (!button || !this.sectionRoot?.contains(button)) return;
            button.disabled = !model.purchasable;
            button.toggleAttribute('aria-disabled', !model.purchasable);
            button.classList.toggle('sold-out--button', !model.purchasable);
            const label =
              button.querySelector('.add-to-cart-text__content') ||
              button.querySelector('.add-to-cart-text') ||
              button.querySelector('span');
            if (label && model.buttonLabel) label.textContent = model.buttonLabel;
          });

          this.productInfo?.querySelectorAll('.accelerated-checkout-block').forEach((checkout) => {
            checkout.hidden = !model.dynamicCheckoutAllowed;
            checkout.inert = !model.dynamicCheckoutAllowed;
            checkout.setAttribute('aria-hidden', String(!model.dynamicCheckoutAllowed));
          });

          if (this.productInfo) this.productInfo.dataset.alvauraPurchaseState = model.state;
          if (model.dynamicCheckoutAllowed) root.Shopify?.PaymentButton?.init?.();
        }

        hasSizeOption() {
          return api.hasSizeOption(this.readVariantPayload());
        }

        sizeGroup() {
          return optionGroups(this.currentVariantPicker()).find((group) =>
            api.isSizeOptionName(group.dataset.alvauraOptionName || group.querySelector('legend, label')?.textContent)
          );
        }

        clearInitialSizeSelection() {
          const group = this.sizeGroup();
          if (!group) return;

          group.querySelectorAll('input[type="radio"]').forEach((input) => {
            input.checked = false;
            input.removeAttribute('checked');
          });
          const select = group.querySelector('select');
          if (select) {
            Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
            select.selectedIndex = -1;
          }
          const selectedLabel = group.querySelector('[data-selected-value]');
          if (selectedLabel) selectedLabel.textContent = 'Select size';
        }

        applyPreferredDefault() {
          if (!api.shouldApplyPreferredDefault({
            defaultSelectionApplied: this.defaultSelectionApplied,
            manualSelectionSeen: this.manualSelectionSeen,
            productLevelMto: this.dataset.productLevelMto === 'true',
            urlVariantId: this.dataset.urlVariantId,
          })) {
            return false;
          }

          this.defaultSelectionApplied = true;
          const payload = this.readVariantPayload();
          const groups = optionGroups(this.currentVariantPicker());
          const selectedValues = this.selectedOptionValues(payload);
          const preferred = api.preferredInitialVariant(payload, selectedValues);
          if (!preferred || api.variantIdsMatch(preferred.id, this.currentFormVariantId())) return false;

          const changedControls = [];
          groups.forEach((group, index) => {
            const optionIndex = optionIndexForGroup(
              group,
              Array.isArray(payload?.optionNames) ? payload.optionNames : [],
              index,
              groups.length
            );
            const desiredValue = optionIndex >= 0 ? preferred.options?.[optionIndex] : null;
            if (desiredValue == null) return;
            const select = group.querySelector('select');
            if (select) {
              const option = Array.from(select.options).find((candidate) => candidate.value === desiredValue);
              if (!option || select.value === desiredValue) return;
              Array.from(select.options).forEach((candidate) => candidate.removeAttribute('selected'));
              option.setAttribute('selected', 'selected');
              select.value = desiredValue;
              changedControls.push(select);
              return;
            }

            const input = Array.from(group.querySelectorAll('input[type="radio"]')).find(
              (candidate) => candidate.value === desiredValue
            );
            if (!input || input.checked) return;
            input.checked = true;
            changedControls.push(input);
          });

          const eventTarget = changedControls[changedControls.length - 1];
          if (!eventTarget) return false;
          this.defaulting = true;
          eventTarget.dispatchEvent(new Event('change', { bubbles: true }));
          Promise.resolve().then(() => {
            this.defaulting = false;
          });
          return true;
        }
      }
    );
  }
})(
  typeof window !== 'undefined' ? window : globalThis,
  function createAlvauraPurchasePathApi() {
    'use strict';

    const STATES = Object.freeze({
      PENDING: 'pending',
      UNAVAILABLE: 'unavailable',
      IN_STOCK: 'in_stock',
      MADE_TO_ORDER: 'made_to_order',
      SOLD_OUT: 'sold_out',
    });

    const COPY = Object.freeze({
      addToCart: 'Add to cart',
      soldOutButton: 'Sold Out',
      unavailableButton: 'Unavailable',
      inStockStatus: 'Ready to ship · Dispatches in 1–3 days',
      madeToOrderStatus: 'Made to order · Ships in 7–14 working days',
      soldOutStatus: 'Sold Out',
      unavailableStatus: 'Unavailable',
    });

    const normalizeWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();
    const normalizeVariantId = (value) => {
      const normalized = String(value ?? '').trim();
      return /^\d+$/.test(normalized) && normalized !== '0' ? normalized : null;
    };
    const variantIdsMatch = (left, right) => {
      const normalizedLeft = normalizeVariantId(left);
      const normalizedRight = normalizeVariantId(right);
      return normalizedLeft != null && normalizedLeft === normalizedRight;
    };
    const urlWithVariantAndView = (href, variantId, view) => {
      const normalizedVariantId = normalizeVariantId(variantId);
      const normalizedView = normalizeWhitespace(view);
      if (!href || !normalizedVariantId || !normalizedView) return null;
      try {
        const url = new URL(href);
        url.searchParams.set('variant', normalizedVariantId);
        url.searchParams.set('view', normalizedView);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (_error) {
        return null;
      }
    };
    const toInventoryInteger = (value) => {
      const normalized = Number(value);
      return Number.isSafeInteger(normalized) ? normalized : null;
    };
    const isSizeOptionName = (value) =>
      normalizeWhitespace(value).replace(/:$/, '').toLowerCase() === 'size';
    const hasSizeOption = (payload) =>
      Array.isArray(payload?.optionNames) && payload.optionNames.some(isSizeOptionName);
    const requiresExplicitSizeSelection = (required, payload) => Boolean(required && hasSizeOption(payload));
    const shouldApplyPreferredDefault = ({
      defaultSelectionApplied = false,
      manualSelectionSeen = false,
      productLevelMto = false,
      urlVariantId = null,
    } = {}) =>
      !defaultSelectionApplied &&
      !manualSelectionSeen &&
      !productLevelMto &&
      normalizeVariantId(urlVariantId) == null;

    const resolveInventoryState = (record) => {
      if (!record || normalizeVariantId(record.variantId) == null) return STATES.UNAVAILABLE;
      // A positive stock count must never override Shopify's actual sellability.
      if (record.available !== true) return STATES.SOLD_OUT;

      if (record.inventoryManagement === 'shopify') {
        const inventoryQuantity = toInventoryInteger(record.inventoryQuantity);
        if (inventoryQuantity == null) return STATES.UNAVAILABLE;
        if (inventoryQuantity > 0) return STATES.IN_STOCK;
        if (record.inventoryPolicy === 'continue') {
          return STATES.MADE_TO_ORDER;
        }
        return STATES.SOLD_OUT;
      }

      return record.available ? STATES.IN_STOCK : STATES.SOLD_OUT;
    };

    const recordForVariant = (variant) => {
      if (!variant) return unavailableRecord();
      const record = {
        variantId: variant.id,
        state: '',
        available: Boolean(variant.available),
        inventoryManagement: variant.inventoryManagement ?? variant.inventory_management ?? null,
        inventoryQuantity: variant.inventoryQuantity ?? variant.inventory_quantity ?? null,
        inventoryPolicy: variant.inventoryPolicy ?? variant.inventory_policy ?? null,
      };
      record.state = resolveInventoryState(record);
      return record;
    };

    const unavailableRecord = () => ({
      variantId: null,
      state: STATES.UNAVAILABLE,
      available: false,
      inventoryManagement: null,
      inventoryQuantity: null,
      inventoryPolicy: null,
    });

    const validatePurchaseRecord = (record) => {
      if (!record || typeof record !== 'object') {
        return { valid: false, reason: 'purchase_state_missing' };
      }
      const resolvedState = resolveInventoryState(record);
      if (record.state !== resolvedState) {
        return { valid: false, reason: 'purchase_state_mismatch', resolvedState };
      }
      return { valid: true, reason: '', resolvedState };
    };

    const viewModelForState = (state) => {
      switch (state) {
        case STATES.IN_STOCK:
          return {
            state,
            status: COPY.inStockStatus,
            buttonLabel: COPY.addToCart,
            purchasable: true,
            madeToOrder: false,
            dynamicCheckoutAllowed: true,
          };
        case STATES.MADE_TO_ORDER:
          return {
            state,
            status: COPY.madeToOrderStatus,
            buttonLabel: COPY.addToCart,
            purchasable: true,
            madeToOrder: true,
            dynamicCheckoutAllowed: false,
          };
        case STATES.SOLD_OUT:
          return {
            state,
            status: COPY.soldOutStatus,
            buttonLabel: COPY.soldOutButton,
            purchasable: false,
            madeToOrder: false,
            dynamicCheckoutAllowed: false,
          };
        case STATES.PENDING:
          return {
            state,
            status: '',
            buttonLabel: '',
            purchasable: false,
            madeToOrder: false,
            dynamicCheckoutAllowed: false,
          };
        case STATES.UNAVAILABLE:
        default:
          return {
            state: STATES.UNAVAILABLE,
            status: COPY.unavailableStatus,
            buttonLabel: COPY.unavailableButton,
            purchasable: false,
            madeToOrder: false,
            dynamicCheckoutAllowed: false,
          };
      }
    };

    const preferredInitialVariant = (payload, selectedValues = []) => {
      if (!payload || !Array.isArray(payload.variants) || !payload.variants.length) return null;
      const optionNames = Array.isArray(payload.optionNames) ? payload.optionNames : [];
      const sizeIndex = optionNames.findIndex(isSizeOptionName);
      const candidates = payload.variants.filter((variant) => {
        if (!Array.isArray(variant.options)) return false;
        return variant.options.every(
          (value, index) => index === sizeIndex || !selectedValues[index] || selectedValues[index] === value
        );
      });
      const records = candidates.map((variant) => ({ variant, state: recordForVariant(variant).state }));
      return (
        records.find(({ state }) => state === STATES.IN_STOCK)?.variant ||
        records.find(({ state }) => state === STATES.MADE_TO_ORDER)?.variant ||
        null
      );
    };

    const isPurchasableState = (state) =>
      state === STATES.IN_STOCK || state === STATES.MADE_TO_ORDER;
    const canSubmitPurchase = (state, currentVariantId, trustedVariantId) =>
      isPurchasableState(state) && variantIdsMatch(currentVariantId, trustedVariantId);

    return Object.freeze({
      STATES,
      COPY,
      normalizeWhitespace,
      normalizeVariantId,
      variantIdsMatch,
      urlWithVariantAndView,
      isSizeOptionName,
      hasSizeOption,
      requiresExplicitSizeSelection,
      shouldApplyPreferredDefault,
      resolveInventoryState,
      recordForVariant,
      unavailableRecord,
      validatePurchaseRecord,
      viewModelForState,
      preferredInitialVariant,
      isPurchasableState,
      canSubmitPurchase,
    });
  }
);
